import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bot, Heart, Zap, Shield, Users, Mail, ArrowRight, Sparkles } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { useLanguage } from '../../contexts/LanguageContext';

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language } = useLanguage();

  const handlePricingClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      const pricingSection = document.getElementById('pricing');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <footer className="relative bg-gradient-to-br from-[var(--bg-primary)] via-[var(--brand-primary)]/5 to-[var(--bg-primary)] border-t border-[var(--border-primary)]/50 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-10 left-10 w-32 h-32 bg-[var(--brand-primary)]/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-24 h-24 bg-[var(--brand-primary)]/5 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Top Section - Brand & Value Prop */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-3 text-[var(--brand-primary)] mb-4">
            <div className="p-2 bg-[var(--brand-primary)]/10 rounded-lg">
              <Bot className="h-8 w-8" />
            </div>
            <div>
              <h3 className="font-bold text-2xl">Invoice-Converter.com</h3>
            </div>
          </div>
          <p className="text-lg text-[var(--text-tertiary)] max-w-2xl mx-auto leading-relaxed">
            {t('footer.missionStatement', { 
              defaultValue: 'Empowering businesses across Europe to effortlessly comply with XRechnung regulations. Join hundreds who\'ve automated their invoice conversion process.' 
            })}
          </p>
          
          {/* Quick stats */}
          <div className="flex justify-center items-center gap-8 mt-8 text-sm">
            <div className="flex items-center gap-2 text-[var(--text-tertiary)]">
              <Zap className="w-4 h-4 text-green-500" />
              <span>{t('footer.stat1', { defaultValue: '20,000+ invoices processed' })}</span>
            </div>
            <div className="flex items-center gap-2 text-[var(--text-tertiary)]">
              <Shield className="w-4 h-4 text-green-500" />
              <span>{t('footer.stat2', { defaultValue: 'Highest accuracy' })}</span>
            </div>
            <div className="flex items-center gap-2 text-[var(--text-tertiary)]">
              <Users className="w-4 h-4 text-green-500" />
              <span>{t('footer.stat3', { defaultValue: 'Trusted by hundreds' })}</span>
            </div>
          </div>
        </div>

        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Services Section */}
          <div className="space-y-6">
            <div>
              <h4 className="font-bold text-lg text-[var(--text-primary)] mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[var(--brand-primary)]" />
                {t('footer.services')}
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link 
                    to={`/${language}/pdf-to-xrechnung`} 
                    className="group flex items-center gap-2 text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] transition-all duration-300"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span className="group-hover:translate-x-1 transition-transform">
                      {t('nav.pdfToXRechnung')}
                    </span>
                  </Link>
                </li>
                <li>
                  <Link 
                    to={`/${language}/pdf-to-cii`} 
                    className="group flex items-center gap-2 text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] transition-all duration-300"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span className="group-hover:translate-x-1 transition-transform">
                      {t('convert.pdfToCii', { defaultValue: 'PDF to XRechnung (CII)' })}
                    </span>
                  </Link>
                </li>
                <li>
                  <Link 
                    to={`/${language}/pdf-to-zugferd`} 
                    className="group flex items-center gap-2 text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] transition-all duration-300"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span className="group-hover:translate-x-1 transition-transform">
                      {t('nav.pdfToZugferd')}
                    </span>
                  </Link>
                </li>
                <li>
                  <Link 
                    to={`/${language}/xrechnung-to-pdf`} 
                    className="group flex items-center gap-2 text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] transition-all duration-300"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span className="group-hover:translate-x-1 transition-transform">
                      {t('nav.xrechnungToPdf')}
                    </span>
                  </Link>
                </li>
                <li>
                  <Link 
                    to={`/${language}/xrechnung-viewer`} 
                    className="group flex items-center gap-2 text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] transition-all duration-300"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span className="group-hover:translate-x-1 transition-transform">
                      {t('nav.xrechnungViewer')}
                    </span>
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Resources Section */}
          <div className="space-y-6">
            <div>
              <h4 className="font-bold text-lg text-[var(--text-primary)] mb-4 flex items-center gap-2">
                <Bot className="w-5 h-5 text-[var(--brand-primary)]" />
                {t('footer.quickLinks')}
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link 
                    to={`/${language}/blog`} 
                    className="group flex items-center gap-2 text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] transition-all duration-300"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span className="group-hover:translate-x-1 transition-transform">
                      {t('nav.blog')}
                    </span>
                  </Link>
                </li>
                <li>
                  <Link 
                    to={`/${language}/changelog`} 
                    className="group flex items-center gap-2 text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] transition-all duration-300"
                    onClick={() => {
                      // Scroll to top when navigating to changelog
                      setTimeout(() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }, 100);
                    }}
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span className="group-hover:translate-x-1 transition-transform">
                      {t('footer.resources.changelog', { defaultValue: 'Product Updates' })}
                    </span>
                  </Link>
                </li>
                <li>
                  <a 
                    href="#pricing" 
                    onClick={handlePricingClick} 
                    className="group flex items-center gap-2 text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] transition-all duration-300"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span className="group-hover:translate-x-1 transition-transform">
                      {t('nav.pricing')}
                    </span>
                  </a>
                </li>
                <li>
                  <a 
                    href="https://climate.stripe.com/Yb7lx2" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="group flex items-center gap-2 text-[var(--text-tertiary)] hover:text-green-500 transition-all duration-300"
                  >
                    <Heart className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity text-green-500" />
                    <span className="group-hover:translate-x-1 transition-transform">
                      {t('footer.climateContribution')}
                    </span>
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Company Section */}
          <div className="space-y-6">
            <div>
              <h4 className="font-bold text-lg text-[var(--text-primary)] mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[var(--brand-primary)]" />
                {t('footer.legal')}
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link 
                    to={`/${language}/imprint`} 
                    className="group flex items-center gap-2 text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] transition-all duration-300"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span className="group-hover:translate-x-1 transition-transform">
                      {t('footer.imprint')}
                    </span>
                  </Link>
                </li>
                <li>
                  <Link 
                    to={`/${language}/privacy-policy`} 
                    className="group flex items-center gap-2 text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] transition-all duration-300"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span className="group-hover:translate-x-1 transition-transform">
                      {t('footer.privacyPolicy')}
                    </span>
                  </Link>
                </li>
                <li>
                  <Link
                    to={`/${language}/dpa`}
                    className="group flex items-center gap-2 text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] transition-all duration-300"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span className="group-hover:translate-x-1 transition-transform">
                      {t('dpa.title')}
                    </span>
                  </Link>
                </li>
                <li>
                  <Link
                    to={`/${language}/terms-and-conditions`}
                    className="group flex items-center gap-2 text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] transition-all duration-300"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span className="group-hover:translate-x-1 transition-transform">
                      Terms &amp; Conditions
                    </span>
                  </Link>
                </li>
                <li>
                  <Link
                    to={`/${language}/money-back-guarantee`}
                    className="group flex items-center gap-2 text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] underline transition-all duration-300"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span className="group-hover:translate-x-1 transition-transform">
                      {t('footer.moneyBackGuarantee')}
                    </span>
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact & CTA Section */}
          <div className="space-y-6">
            <div>
              <h4 className="font-bold text-lg text-[var(--text-primary)] mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-[var(--brand-primary)]" />
                {t('footer.contact.title', { defaultValue: 'Get in Touch' })}
              </h4>
              <div className="space-y-4">
                <a 
                  href="mailto:contact@invoice-converter.com" 
                  className="group flex items-center gap-2 text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] transition-all duration-300"
                >
                  <Mail className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-1 transition-transform">
                    contact@invoice-converter.com
                  </span>
                </a>
                
                {/* CTA Box */}
                <div className="mt-6">
                  <Link
                    to={`/${language}/convert`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--brand-primary)] text-[var(--text-button)] text-sm font-semibold rounded-lg hover:opacity-90 transition-all duration-300 transform hover:scale-105"
                  >
                    <Zap className="w-4 h-4" />
                    {t('footer.cta.button', { defaultValue: 'Start Converting' })}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-[var(--border-primary)]/50 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-[var(--text-tertiary)] text-sm flex items-center gap-2">
              <span>&copy; 2025 Invoice-Converter.com.</span>
              <span className="flex items-center gap-1">
                {t('footer.madeWith', { defaultValue: 'Made with' })} <Heart className="w-3 h-3 text-red-500 fill-current" /> {t('footer.madeLocation', { defaultValue: 'in Europe' })}
              </span>
            </div>
            
            {/* Trust indicators */}
            <div className="flex items-center gap-4 text-xs text-[var(--text-tertiary)]">
              <div className="flex items-center gap-1">
                <Shield className="w-3 h-3 text-green-500" />
                <span>{t('footer.gdprCompliant', { defaultValue: 'GDPR Compliant' })}</span>
              </div>
              <div className="flex items-center gap-1">
                <Zap className="w-3 h-3 text-green-500" />
                <span>{t('footer.uptime', { defaultValue: '&gt;99% Uptime' })}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;